<template>
  <div>
    <div>
      <!-- page meta -->
      <Head>
        <title>Membership Software by Platforma</title>
        <link
          rel="icon"
          :href="$filters.public_path('favicon.png')"
          type="favicon"
        />
        <meta
          name="description"
          content="A complete all-in-one membership site software to manage your online courses, coaching clients, fans and subscribers"
        />
      </Head>
    </div>
    <!-- content start -->

    <div
      class="min-[1140px]:max-w-[1140px] container relative mx-auto bg-transparent"
    >
      <!-- ===================== HERO SECTION ===================== -->

      <section
        id="section1"
        class="hero-section relative mx-auto mt-[150px] justify-center gap-1 bg-transparent lg:flex lg:py-0"
        :style="backgroundImageStyleMain"
      >
        <div class="mt-0 bg-transparent px-[25px] xl:px-0">
          <h1
            class="top-full mt-0 px-0 text-[35px] font-extrabold tracking-normal text-[#292d34] sm:text-[40px] lg:text-[45px] lg:leading-[69px] xl:text-[50px]"
          >
            Manage members<br />like a boss.
          </h1>

          <p
            class="mt-[5px] mb-[25px] text-[15px] font-medium text-[#7c828d] lg:text-lg"
          >
            Save time on operations so you can focus on scaling your business.
          </p>

          <!-- captcher email widget -->
          <EmailCaptureHeaderWidget />

          <img :src="$filters.media_path('/images/stars.svg')" alt="" />
        </div>

        <div class="mb-[30px] mt-[30px] lg:mt-0 z-10 px-[20px]">
          <video
            class="lg:max-w-[618px] rounded-[10px] lg:rounded-[20px] shadow-2xl"
            id="home_video"
            loop
            muted
            autoplay
            playsinline
          >
            <source
              :src="
                $filters.media_path(
                  '/videos/member-management-list-creator-dashboard-com.mp4'
                )
              "
              type="video/mp4"
            />
          </video>
        </div>
      </section>

      <div
        class="join-mobile mx-auto mt-[30px] h-[150px] max-w-6xl gap-2 bg-[#f5f8fa] py-[65px] md:mt-[150px] md:flex md:h-auto md:bg-[#fafbfc] md:bg-blend-overlay"
      >
        <div class="m-auto hidden h-[1px] w-[400px] bg-[#e9ebf0] md:flex"></div>
        <h2
          class="mx-auto px-0 text-center text-[12px] font-[500] text-[#7c828d]"
        >
          JOIN 10,000+ HIGH EARNING BUSINESSES & CREATORS
        </h2>
        <div class="m-auto hidden h-[1px] w-[400px] bg-[#e9ebf0] md:flex"></div>
      </div>

      <!-- ===================== MONETIZATION SECTION ===================== -->
      <section
        class="relative mt-[70px] w-full justify-center gap-2 px-[25px] lg:mx-auto lg:mt-[50px] lg:flex lg:h-[500px] xl:px-0"
      >
        <img
          class="absolute top-10 left-[600px] z-0 hidden h-[600px] w-[810px] origin-top-right lg:block"
          :src="$filters.media_path('/images/blue_gradient.svg')"
          alt=""
        />

        <div class="z-20 mx-auto mt-12 justify-center gap-1 py-0 lg:flex">
          <div class="mt-0 text-center lg:max-w-[500px] lg:text-left">
            <div
              id="text_monetize"
              class="text-xs font-extrabold text-[#2D8CFF]"
            >
              MONETIZATION
            </div>

            <h2
              class="top-full mt-[20px] px-0 text-[35px] font-bold text-[#292d34] sm:text-[40px] lg:text-[45px] lg:leading-[69px] xl:text-[50px]"
            >
              Full checkout for member onboarding.
            </h2>

            <hr
              class="m-auto mt-[30px] h-[5px] w-[100px] rounded-full bg-[#2D8CFF] lg:ml-0"
            />

            <p
              class="mt-[50px] text-sm font-medium leading-[25px] text-[#7c828d] xl:max-w-[440px]"
            >
              Sell with options. All while keeping money in your pocket. ZERO
              Transaction fees. Link up a payment processor and start bringing
              in paying members to your site.
            </p>
          </div>

          <div class="z-30 mt-[25px] xl:mt-0">
            <video
              class="lg:max-w-[648px] rounded-[10px] lg:rounded-[20px] shadow-2xl"
              id="home_video"
              loop
              muted
              autoplay
              playsinline
            >
              <source
                :src="$filters.media_path('/videos/checkout-page-com.mp4')"
                type="video/mp4"
              />
            </video>
          </div>
        </div>
      </section>

      <!-- ===================== DELIVERABLES SECTION ===================== -->
      <section
        class="relative mt-[70px] w-full justify-center gap-2 px-[25px] lg:mx-auto lg:mt-[150px] lg:flex lg:h-[500px] lg:flex-row-reverse xl:px-0"
      >
        <img
          class="absolute top-0 right-[600px] z-0 h-[600px] w-[810px] origin-top-left overflow-hidden"
          :src="$filters.media_path('/images/green-diamond-dots-bg.svg')"
          alt=""
        />

        <div
          class="mt-12 text-center lg:mx-0 lg:mt-0 lg:max-w-[500px] lg:px-[30px] lg:text-left"
        >
          <div
            id="text_deliverables"
            class="text-xs font-extrabold text-[#27AE60] lg:left-0"
          >
            DELIVERABLES
          </div>

          <h2
            class="top-full px-0 text-[35px] font-bold text-[#292d34] sm:text-[40px] md:mt-[20px] lg:text-[45px] lg:leading-[69px] xl:text-[50px]"
          >
            More content and revenue flexibility.
          </h2>

          <div class="">
            <hr
              class="m-auto mt-[30px] h-[5px] w-[100px] rounded-full bg-[#27AE60] lg:ml-0"
            />
          </div>

          <p
            class="mt-[50px] text-sm font-medium leading-[25px] text-[#7c828d] xl:max-w-[440px]"
          >
            Get subscribers for and upsell any form of content; courses, posts,
            podcasts, physical products, videos, blogs, and more.
          </p>
        </div>

        <div
          class="mx-auto mt-12 justify-center gap-1 py-0 md:z-10 lg:mt-0 lg:px-0"
        >
          <img
            loading="lazy"
            class="lg:w-[656px]"
            :src="
              $filters.media_path(
                '/images/advanced_analytics_to_own_and_monetize_member_data.png'
              )
            "
            alt="platform analytics dashboard allows you to track everything within your site."
          />
        </div>
      </section>

      <!-- ===================== PROTECTION-CENSORSHIP SECTION ===================== -->
      <section
        class="relative mt-[70px] w-full gap-2 px-[25px] lg:mx-auto lg:mt-[150px] lg:flex lg:h-[550px] xl:px-0"
      >
        <div class="mt-0 text-center lg:max-w-[500px] lg:text-left">
          <div
            id="text_protection"
            class="left-0 text-xs font-extrabold text-[#49CCF9]"
          >
            PROTECTION
          </div>

          <h2
            class="top-full mt-[20px] px-0 text-[35px] font-bold text-[#292d34] sm:text-[40px] lg:text-[45px] lg:leading-[69px] xl:text-[50px]"
          >
            Safe-guard your business from online censorship.
          </h2>

          <hr
            class="m-auto mt-[30px] h-[5px] w-[100px] rounded-full bg-[#49CCF9] lg:ml-0"
          />

          <p
            class="mt-[50px] text-sm font-medium leading-[25px] text-[#7c828d] xl:max-w-[440px]"
          >
            Most membership site services run on big tech stacks that are
            subject to terms of service, community guidelines, and censorship.
            Platforma runs its own data centers to ensure you post with freedom.
          </p>
        </div>

        <div class="mt-[40px] bg-transparent">
          <img
            loading="lazy"
            class="top-10 lg:absolute lg:left-[500px] lg:w-[835px] lg:origin-top-right lg:overflow-hidden"
            :src="
              $filters.media_path(
                '/images/platforma_map_secure_data_guard_against_censorship.png'
              )
            "
            alt="platforma data center map where you own your data to protect against online censorship"
          />
        </div>
      </section>
    </div>
    <!-- ===================== CTA BLOCK ===================== -->
    <CtaBlock
      cta-headline="Give more value to your members
starting today."
    />
  </div>
</template>

<!-- =======CONTENT END======== -->

<script>
import DefaultLayout from '../Layouts/DefaultLayout.vue';
import ButtonComponent from '@shared/Components/ButtonComponent.vue';
import Axios from 'axios';
import EmailCaptureHeaderWidget from '../Components/EmailCaptureHeader.vue';
import CtaBlock from '../Components/CtaBlockFooterComponent.vue';

export default {
  layout: DefaultLayout,
  components: {
    // ButtonComponent,
    EmailCaptureHeaderWidget,
    CtaBlock
  },

  computed: {
    backgroundImageStyleMain() {
      const imagePath = this.$filters.media_path(
        'images/platforma-header-background-eclipse.svg'
      );
      return {
        '--before-background-image': `url(${imagePath})`
      };
    }
  }
};
</script>

<style scoped>
.hero-section::before {
  content: '';
  position: absolute;
  top: 0;
  height: 1000px;
  width: 1700px;
  display: block;
  z-index: 0;
  transform: translateY(-42%) translateX(58%);
  background-repeat: no-repeat;
  background-image: var(--before-background-image);
}

.join-mobile {
  background-image: url(../media/images/two-dots.svg);
  background-size: contain;
}
</style>
